import {
    type ApiError,
    type SlackAppCustomSettings,
    type SlackChannel,
    type SlackFeatureAvailabilityMap,
    type SlackSettings,
} from '@lightdash/common';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { type LightdashApi } from '../../api';
import { useLightdashApi } from '../../providers/LightdashApi/useLightdashApi';
import useToaster from '../toaster/useToaster';

// An API pod from before `features` shipped omits it during a rolling update
type LegacySlackSettings = Omit<SlackSettings, 'features'> & {
    features?: SlackFeatureAvailabilityMap;
};

export const normalizeSlackSettings = (
    raw: LegacySlackSettings | undefined,
): SlackSettings | undefined => {
    if (raw === undefined) return undefined;
    if (raw.features) return { ...raw, features: raw.features };
    const fallback = { available: raw.hasRequiredScopes, missingScopes: [] };
    return {
        ...raw,
        features: {
            scheduledDeliveries: fallback,
            customAppProfile: fallback,
            linkUnfurling: fallback,
            writebackReactions: fallback,
            aiAgent: fallback,
        },
    };
};

const getSlack = async (lightdashApi: LightdashApi) =>
    normalizeSlackSettings(
        await lightdashApi<SlackSettings>({
            url: `/slack/`,
            method: 'GET',
            body: undefined,
        }),
    );

export const useGetSlack = () => {
    const lightdashApi = useLightdashApi();
    return useQuery<SlackSettings | undefined, ApiError>({
        queryKey: ['slack'],
        queryFn: () => getSlack(lightdashApi),
        retry: false,
    });
};

const deleteSlack = async (lightdashApi: LightdashApi) =>
    lightdashApi<null>({
        url: `/slack/`,
        method: 'DELETE',
        body: undefined,
    });

export const useDeleteSlack = () => {
    const lightdashApi = useLightdashApi();

    const queryClient = useQueryClient();
    const { showToastSuccess, showToastApiError } = useToaster();
    return useMutation<null, ApiError, undefined>(
        () => deleteSlack(lightdashApi),
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(['slack']);

                showToastSuccess({
                    title: `Deleted! Slack integration was deleted`,
                });
            },
            onError: ({ error }) => {
                showToastApiError({
                    title: `Failed to delete Slack integration`,
                    apiError: error,
                });
            },
        },
    );
};

const getSlackChannels = async (
    lightdashApi: LightdashApi,
    {
        search,
        excludeArchived,
        excludeDms,
        excludeGroups,
        forceRefresh,
        includeChannelIds,
    }: {
        search: string;
        excludeArchived: boolean;
        excludeDms: boolean;
        excludeGroups: boolean;
        forceRefresh: boolean;
        includeChannelIds?: string[];
    },
) => {
    const queryString = new URLSearchParams();
    queryString.set('search', search);
    queryString.set('excludeArchived', excludeArchived.toString());
    queryString.set('excludeDms', excludeDms.toString());
    queryString.set('excludeGroups', excludeGroups.toString());
    queryString.set('forceRefresh', forceRefresh.toString());
    if (includeChannelIds && includeChannelIds.length > 0) {
        queryString.set('includeChannelIds', includeChannelIds.join(','));
    }

    return lightdashApi<SlackChannel[] | undefined>({
        url: `/slack/channels?${queryString.toString()}`,
        method: 'GET',
        body: undefined,
    });
};

export const useSlackChannels = (
    search: string,
    {
        excludeArchived = true,
        excludeDms = false,
        excludeGroups = false,
        includeChannelIds,
    }: {
        excludeArchived?: boolean;
        excludeDms?: boolean;
        excludeGroups?: boolean;
        includeChannelIds?: string[];
    },
    useQueryOptions?: UseQueryOptions<SlackChannel[] | undefined, ApiError>,
) => {
    const lightdashApi = useLightdashApi();

    const queryClient = useQueryClient();
    const [isRefreshing, setIsRefreshing] = useState(false);

    const query = useQuery<SlackChannel[] | undefined, ApiError>({
        queryKey: [
            'slack_channels',
            search,
            excludeArchived,
            excludeDms,
            excludeGroups,
            includeChannelIds,
        ],
        queryFn: () =>
            getSlackChannels(lightdashApi, {
                search,
                excludeArchived,
                excludeDms,
                excludeGroups,
                forceRefresh: false,
                includeChannelIds,
            }),
        ...useQueryOptions,
    });

    const refresh = useCallback(async () => {
        setIsRefreshing(true);
        const slackChannelsAfterRefresh = await getSlackChannels(lightdashApi, {
            search,
            excludeArchived,
            excludeDms,
            excludeGroups,
            forceRefresh: true,
            includeChannelIds,
        });
        queryClient.setQueryData(
            [
                'slack_channels',
                search,
                excludeArchived,
                excludeDms,
                excludeGroups,
                includeChannelIds,
            ],
            slackChannelsAfterRefresh,
        );
        setIsRefreshing(false);
    }, [
        search,
        excludeArchived,
        excludeDms,
        excludeGroups,
        includeChannelIds,
        queryClient,
        lightdashApi,
    ]);

    return {
        ...query,
        isRefreshing: query.isFetching || isRefreshing,
        refresh,
    };
};

const getSlackChannelById = async (
    lightdashApi: LightdashApi,
    channelId: string,
) =>
    lightdashApi<SlackChannel | null>({
        url: `/slack/channels/${encodeURIComponent(channelId)}`,
        method: 'GET',
        body: undefined,
    });

/**
 * Hook for on-demand channel lookup when user pastes a channel ID
 */
export const useSlackChannelLookup = () => {
    const lightdashApi = useLightdashApi();

    const queryClient = useQueryClient();

    return useMutation<SlackChannel | null, ApiError, string>({
        mutationFn: (channelId: string) =>
            getSlackChannelById(lightdashApi, channelId),
        onSuccess: async (channel) => {
            if (channel) {
                // Invalidate channels queries to include the new channel
                await queryClient.invalidateQueries({
                    queryKey: ['slack_channels'],
                    refetchType: 'none', // Don't refetch, just mark as stale
                });
            }
        },
    });
};

const updateSlackCustomSettings = async (
    lightdashApi: LightdashApi,
    opts: SlackAppCustomSettings,
) =>
    lightdashApi<null>({
        url: `/slack/custom-settings`,
        method: 'PUT',
        body: JSON.stringify({
            ...opts,
            appProfilePhotoUrl: opts.appProfilePhotoUrl || null,
        }),
    });

export const useUpdateSlackAppCustomSettingsMutation = () => {
    const lightdashApi = useLightdashApi();

    const queryClient = useQueryClient();
    const { showToastSuccess, showToastApiError } = useToaster();
    return useMutation<null, ApiError, SlackAppCustomSettings>(
        (opts: SlackAppCustomSettings) =>
            updateSlackCustomSettings(lightdashApi, opts),
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(['slack']);

                showToastSuccess({
                    title: `Success! Slack app settings updated`,
                });
            },
            onError: ({ error }) => {
                showToastApiError({
                    title: `Failed to update Slack app settings`,
                    apiError: error,
                });
            },
        },
    );
};
